import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { apiUrl } from '../../config/api';
import apiInstance from '../../config/api';

const rawApi = axios.create({
    baseURL: apiUrl,
    withCredentials: true,
});

export const getUserSettings = createAsyncThunk('settings/getUserSettings', async (_, { rejectWithValue }) => {
    try {
        const response = await apiInstance.get('/api/user/user/data')
        return response.data;
    } catch (error) {
        console.log('setting error; ', error.response?.data?.message || error.message)
        return rejectWithValue(error.response?.data?.message || error.message);
    }
});

// `method` is optional - omit it to confirm the account's current method (the
// disable case); pass it explicitly when confirming an enable, since 2FA is
// off at that point and there's no current method to default to.
export const send2faCode = createAsyncThunk('settings/send2faCode', async (method, { rejectWithValue }) => {
    try {
        const response = await apiInstance.put('/api/user/send2fa', method ? { method } : {})
        return response.data;
    } catch (error) {
        console.log('2facode failed; ', error.response?.data?.message || error.message)
        return rejectWithValue(error.response?.data?.message || error.message);
    }
});

// Confirms the code from send2faCode (or a live authenticator/backup code)
// for a settings action - zero cookie/token/session side effects, unlike
// verify/verifyLogin which mints a fresh session and would log the user out
// mid-settings-change.
export const verify2faAction = createAsyncThunk('settings/verify2faAction', async ({ otp, code }, { rejectWithValue }) => {
    try {
        const response = await apiInstance.post('/api/user/verify-2fa-action', code !== undefined ? { code } : { otp })

        if (response.data.status === 'success') {
            return response.data;
        } else {
            throw new Error(response.data.message || 'Verification failed')
        }
    }
    catch (error) {
        console.log('verify error: ', error.response?.data?.message || error.message)
        return rejectWithValue(error.response?.data?.message || error.message)
    }
})

export const toggleTwoFA = createAsyncThunk('settings/toggleTwoFA', async (method, { rejectWithValue }) => {
    try {
        const response = await apiInstance.post('/api/user/toggle-2fa', { method })
        return response.data;
    } catch (error) {
        console.log('toggle 2fa error: ', error.response?.data?.message || error.message)
        return rejectWithValue(error.response?.data?.message || error.message);
    }
});

export const getTwoFADetails = createAsyncThunk('settings/getTwoFADetails', async (userId, { rejectWithValue }) => {
    try {
        const response = await apiInstance.get(`/api/user/${userId}/twoFA`)
        return response.data;
    } catch (error) {
        console.log('get 2fa details error: ', error.response?.data?.message || error.message)
        return rejectWithValue(error.response?.data?.message || error.message);
    }
});

export const setupAuthenticator = createAsyncThunk('settings/setupAuthenticator', async (_, { rejectWithValue }) => {
    try {
        const response = await apiInstance.post('/api/user/authenticator/setup')
        return response.data;
    } catch (error) {
        console.log('authenticator setup error: ', error.response?.data?.message || error.message)
        return rejectWithValue(error.response?.data?.message || error.message);
    }
});

export const verifyAuthenticator = createAsyncThunk('settings/verifyAuthenticator', async (token, { rejectWithValue }) => {
    try {
        const response = await apiInstance.post('/api/user/authenticator/verify', { token })
        return response.data;
    } catch (error) {
        console.log('authenticator verify error: ', error.response?.data?.message || error.message)
        return rejectWithValue(error.response?.data?.message || error.message);
    }
});

export const disableAuthenticator = createAsyncThunk('settings/disableAuthenticator', async (_, { rejectWithValue }) => {
    try {
        const response = await apiInstance.post('/api/user/authenticator/disable')
        return response.data;
    } catch (error) {
        console.log('authenticator disable error: ', error.response?.data?.message || error.message)
        return rejectWithValue(error.response?.data?.message || error.message);
    }
});

// ✅ FIXED: Now sends socialData to the backend
export const updateSocialMedia = createAsyncThunk('settings/updateSocialMedia', async (socialData, { rejectWithValue }) => {
    try {
        const response = await apiInstance.post('/api/socials/social-data', socialData)  // ← Added socialData as body
        return response.data.socialData;
    } catch (error) {
        console.error('Error updating social media:', error.response?.data?.message || error.message);
        return rejectWithValue(error.response?.data?.message || error.message);
    }
});

export const toggleAccountType = createAsyncThunk('settings/toggleAccountType', async (_, { rejectWithValue }) => {
    try {
        const response = await apiInstance.put('/api/user/account/type')
        return response.data.message.includes('private') ? 'private' : 'public';
    } catch (error) {
        console.error('Error toggling account type:', error.response?.data?.message || error.message);
        return rejectWithValue(error.response?.data?.message || error.message);
    }
});

// ✅ FIXED: Now sends visibility data to the backend
export const toggleVisibility = createAsyncThunk('settings/toggleVisibility', async (visible, { rejectWithValue }) => {
    try {
        const response = await apiInstance.patch('/api/user/toggle-visibility/visibility', { visible })  // ← Added visible as body
        return response.data.visibility;
    } catch (error) {
        console.error('Error toggling visibility:', error.response?.data?.message || error.message);
        return rejectWithValue(error.response?.data?.message || error.message);
    }
});

export const toggleOnlineStatusVisibility = createAsyncThunk('settings/toggleOnlineStatusVisibility', async (visible, { rejectWithValue }) => {
    try {
        const response = await rawApi.patch('/api/user/toggle-visibility/visibility', {
            visible,
            type: 'onlineStatus'
        });
        return response.data.onlineVisibility ?? response.data.visibility ?? visible;
    } catch (error) {
        const axiosError = error;
        const status = axiosError.response?.status;
        const backendMessage = axiosError.response?.data?.message || axiosError.message || 'Failed to update online status visibility';

        if (status === 404) {
            try {
                const fallbackResponse = await rawApi.post('/api/user/profileupdate', { onlineVisibility: visible });
                return fallbackResponse.data?.data?.onlineVisibility ?? fallbackResponse.data?.data?.visibility ?? visible;
            } catch (fallbackError) {
                const fallbackMessage = fallbackError.response?.data?.message || fallbackError.message || 'Fallback update failed';
                console.error('Fallback online visibility update failed:', fallbackMessage, fallbackError);
                return rejectWithValue(fallbackMessage);
            }
        }

        console.error('Error toggling online status visibility:', backendMessage, error);
        return rejectWithValue(backendMessage);
    }
});